import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Brain, Code, Lightbulb, ArrowRight, CheckCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { trackButtonClick } from '../utils/analytics';

const iconMap = {
  brain: Brain,
  code: Code,
  lightbulb: Lightbulb
};

const Home = () => {
  const videoRef = useRef(null);
  const [videoError, setVideoError] = React.useState(false);
  const [videoSrc, setVideoSrc] = React.useState(null);
  const { t, language } = useLanguage();
  
  const getPath = (path) => {
    return language === 'en' ? `/en${path}` : path;
  };

  useEffect(() => {
    // Simple direct approach - let browser handle it natively
    // Fallback is acceptable for dev environment
    setVideoSrc('/hero-video.mp4');
    
    // Set a timeout to show fallback if video doesn't load in 5 seconds
    const fallbackTimer = setTimeout(() => {
      if (videoRef.current && videoRef.current.readyState < 3) {
        console.log('Video loading timeout - using fallback');
        setVideoError(true);
      }
    }, 5000);

    return () => {
      clearTimeout(fallbackTimer);
    };
  }, []);

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden min-h-screen flex items-center">
        {/* Video Background with Fallback */}
        <div className="absolute inset-0 bg-black">
          {!videoError && videoSrc ? (
            <video
              ref={videoRef}
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              src={videoSrc}
              className="w-full h-full object-cover md:object-cover object-contain"
              style={{objectPosition: 'center'}}
              onError={() => {
                console.log('Video playback error');
                setVideoError(true);
              }}
              onLoadedData={(e) => {
                console.log('Video loaded and ready');
                e.target.play().catch(err => {
                  console.log('Autoplay issue:', err);
                });
              }}
            />
          ) : (
            // Premium Animated Gradient Fallback
            <>
              {/* Base gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-black via-[#0a0a0a] to-black"></div>
              
              {/* Animated glowing orbs */}
              <div className="absolute inset-0 opacity-50">
                <div 
                  className="absolute w-96 h-96 md:w-[600px] md:h-[600px] bg-[#D6CE15]/20 rounded-full blur-3xl animate-float-slow" 
                  style={{top: '15%', left: '50%', transform: 'translateX(-50%)'}}
                />
                <div 
                  className="absolute w-96 h-96 md:w-[500px] md:h-[500px] bg-[#A4A71E]/20 rounded-full blur-3xl animate-float-medium" 
                  style={{top: '55%', right: '5%'}}
                />
                <div 
                  className="absolute w-80 h-80 md:w-[450px] md:h-[450px] bg-[#D6CE15]/25 rounded-full blur-3xl animate-float-fast" 
                  style={{bottom: '5%', left: '5%'}}
                />
                <div 
                  className="absolute w-64 h-64 md:w-96 md:h-96 bg-[#53900F]/15 rounded-full blur-2xl animate-float-slow" 
                  style={{top: '40%', left: '20%', animationDelay: '2s'}}
                />
              </div>
              
              {/* Subtle grid overlay */}
              <div className="absolute inset-0 opacity-[0.08]" style={{
                backgroundImage: 'linear-gradient(#D6CE15 1px, transparent 1px), linear-gradient(90deg, #D6CE15 1px, transparent 1px)',
                backgroundSize: '50px 50px'
              }}></div>
              
              {/* Radial gradient overlay for depth */}
              <div className="absolute inset-0 bg-gradient-radial from-transparent via-black/40 to-black/80"></div>
            </>
          )}
        </div>
        
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/60" />
        
        <div className="container mx-auto relative z-10 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6 leading-tight px-2 break-words hyphens-auto" lang="et">
              {t.hero.title.includes('&') ? (
                <>
                  <span className="text-white">{t.hero.title.split('&')[0]}</span>
                  <span className="text-[#D6CE15] neon-text">&</span>
                  <span className="text-white">{t.hero.title.split('&')[1]}</span>
                </>
              ) : (
                <span className="text-white">{t.hero.title}</span>
              )}
            </h1>
            <p className="text-base md:text-lg lg:text-xl text-gray-300 mb-8 md:mb-10 leading-relaxed max-w-3xl mx-auto px-4">
              {t.hero.subtitle}
            </p>
            <Link
              to={getPath('/contact')}
              onClick={() => trackButtonClick('hero_cta', 'hero_section')}
              className="inline-flex items-center space-x-2 px-6 md:px-8 py-3 md:py-4 bg-[#D6CE15] text-black rounded-lg font-semibold text-base md:text-lg hover:bg-[#A4A71E] transition-all duration-200 hover:shadow-2xl hover:shadow-[#D6CE15]/50 hover:-translate-y-1 group"
            >
              <span>{t.hero.cta}</span>
              <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="py-12 md:py-20 px-4 md:px-6 bg-[#0a0a0a]">
        <div className="container mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 md:mb-4">{t.expertise.title.split(' ')[0]} <span className="text-[#D6CE15] neon-text">{t.expertise.title.split(' ').slice(1).join(' ')}</span></h2>
            <p className="text-gray-400 text-base md:text-lg px-4">{t.expertise.subtitle}</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {t.expertise.items.map((item) => {
              const Icon = iconMap[item.icon];
              return (
                <div
                  key={item.id}
                  className="group bg-[#111111] border border-[#D6CE15]/20 rounded-2xl p-6 md:p-8 hover:border-[#D6CE15] transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-[#D6CE15]/30 relative overflow-hidden"
                >
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#D6CE15] to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-[#D6CE15]/10 rounded-xl flex items-center justify-center mb-4 md:mb-6 group-hover:bg-[#D6CE15]/20 transition-colors border border-[#D6CE15]/30">
                    <Icon className="text-[#D6CE15]" size={24} />
                  </div>
                  <h3 className="text-lg md:text-xl font-semibold text-white mb-3 md:mb-4">{item.title}</h3>
                  <p className="text-sm md:text-base text-gray-400 leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-12 md:py-20 px-4 md:px-6 bg-black">
        <div className="container mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 md:mb-4">{t.approach.title}</h2>
            <p className="text-gray-400 text-base md:text-lg px-4">{t.approach.subtitle}</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 max-w-6xl mx-auto">
            {t.approach.points.map((point) => (
              <div
                key={point.id}
                className="bg-[#0a0a0a] border border-[#D6CE15]/10 rounded-xl p-5 md:p-6 hover:bg-[#111111] hover:border-[#D6CE15]/30 transition-all duration-300"
              >
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-[#D6CE15] flex-shrink-0 mt-1" size={18} />
                  <div>
                    <h4 className="text-white font-semibold mb-2 text-sm md:text-base">{point.title}</h4>
                    <p className="text-gray-400 text-xs md:text-sm leading-relaxed">{point.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-24 px-4 md:px-6 bg-[#0a0a0a]">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] rounded-2xl p-8 md:p-12 relative overflow-hidden border border-[#D6CE15]/20">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#D6CE15]/20 rounded-full blur-3xl" />
            <div className="relative z-10">
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-3 md:mb-4 px-2">
                {t.ctaSection.title}
              </h2>
              <p className="text-gray-300 text-base md:text-lg mb-6 md:mb-8 px-4">
                {t.ctaSection.subtitle}
              </p>
              <Link
                to={getPath('/contact')}
                onClick={() => trackButtonClick('bottom_cta', 'cta_section')}
                className="inline-flex items-center space-x-2 px-6 md:px-8 py-3 md:py-4 bg-[#D6CE15] text-black rounded-lg font-semibold text-base md:text-lg hover:bg-[#A4A71E] transition-all duration-200 hover:shadow-2xl hover:shadow-[#D6CE15]/50 hover:-translate-y-1 group"
              >
                <span>{t.ctaSection.cta}</span>
                <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;